/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2022. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: OnlineUserManager.ts
 * Created by: ChanYing
 * Create Date: 2022-11-23 09:34
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import {OnlineUser} from "@/security/OnlineUser";
import Log from "@/commons/logs/Log";
import {StatusDef} from "@/commons/StatusDef";
import Result from "@/commons/Result";
import {reactive} from "vue";
import type {UnwrapNestedRefs} from "vue";
import {TeamInfoVO} from "@/mvc/models/team/TeamInfoVO";
import PageHelper from "@/services/PageHelper";
import TeamService from "@/services/TeamService";
import UserService from "@/services/UserService";
import ProfileData from "@/utils/cookie/ProfileData";

export default class OnlineUserManager {
    // ------------------------------------------------------------------------------------
    // singleton
    // ------------------------------------------------------------------------------------
    private static _instance: OnlineUserManager = new this();

    public static get Instance() {
        return this._instance;
    }

    // ------------------------------------------------------------------------------------

    private readonly onlineUser: OnlineUser;

    // ------------------------------------------------------------------------------------

    private constructor() {
        this.onlineUser = new OnlineUser();
    }

    /**
     * Updates the current online user's team information with the provided data.
     *
     * @param {TeamInfoVO} teamInfo - The team information object that contains updated data for the team's details.
     * @return {Promise<Result<void>>} A promise that resolves to a `Result` object indicating the success or failure of the operation.
     */
    public async updateTeamInfo(teamInfo: TeamInfoVO): Promise<Result<void>> {
        Log.debug("update team info to current online user ......")
        if (teamInfo == null) {
            Log.error("failed to update team info, cause: team info is empty.");
            return Promise.resolve(Result.of<void>(StatusDef.ErrError));
        }
        this.onlineUser.teamInfo = teamInfo;

        // current user — mandatory: without it the session is unusable
        const userRes = await UserService.Instance.retrieve(teamInfo.userId);
        if (!userRes.isHttpStatus2xx() || userRes.data == null) {
            Log.error("failed to retrieve current user info, userId: " + teamInfo.userId);
            return Result.of<void>(StatusDef.ErrError);
        }
        this.onlineUser.userInfo = userRes.data
        this.onlineUser.userInfo.avatar = UserService.Instance.getAvatar(teamInfo.userId);

        if (this.onlineUser.userInfo.profileSetting != null) {
            ProfileData.setLanguage(this.onlineUser.userInfo.profileSetting.language);
            ProfileData.setAppearance(this.onlineUser.userInfo.profileSetting.appearance);
            ProfileData.setPageSize(this.onlineUser.userInfo.profileSetting.pageSize);
            ProfileData.setAccent(this.onlineUser.userInfo.profileSetting.accent);
        }

        // region — cosmetic: a failure must not block login
        //
        // The continent is deliberately not fetched, and TeamInfo no longer carries one at all.
        //
        // team-site stopped holding an org's continent on 2026-08-27: dnsd DERIVES it from the
        // region, so a copy here would be a second source of truth that goes stale and then reads
        // as authoritative. The field first went to ContinentUnknown, which made this code ask for
        // the name of continent 0 and render the literal "Unknown-Continent(0)" in the footer;
        // then geoContinentId was removed from the API model entirely.
        //
        // If a continent is wanted here again it has to be asked of dnsd. Do not reinstate a
        // TeamInfo field for it — that is the arrangement this removed.
        const regionNameRes = await TeamService.Instance.getRegionName(teamInfo.geoRegionId);
        this.onlineUser.teamInfo.regionName = regionNameRes.data ?? "";

        return Result.of<void>(StatusDef.Success);
    }

    /**
     * Logs out the current online user, resets user data, and redirects to the logout page.
     */
    public logout() {
        Log.debug("on OnlineUserManager::logout() ...... user-id: " + this.onlineUser.userInfo?.userId);
        // const fqdn = this.getTeamFqdn();
        this.onlineUser.reset();
        PageHelper.Instance.goToLogout();
    }

    public isAuthorized(): boolean {
        return this.onlineUser.isAuthorized();
    }

    public get(): UnwrapNestedRefs<OnlineUser> {
        return reactive(this.onlineUser);
    }

    public getTeamFqdn(): string {
        if (this.onlineUser.teamInfo == null || this.onlineUser.teamInfo.teamFqdn == null) {
            return "";
        }
        return this.onlineUser.teamInfo.teamFqdn;
    }
}